// The one list of the home dashboard's notice cards, and the rules about which of
// them may be drawn at the same time.
//
// The dashboard shows a stack of small `cs-info-card` notices above the day's item
// list. They have nothing to do with one another — a weigh-in nag, a billing
// warning, an update prompt — and each is wired into CsDayItemsComponent by hand.
// This module owns the part they DO share: which notices exist, the order they are
// drawn in, and which of them may coexist. Before it existed that knowledge was
// spread across nine separate `@if (atToday && showX)` blocks in the template, so
// adding a card meant reading the whole template to find out whether it could
// collide with an existing one. #4455 had to merge two cards after discovering
// exactly that collision, and the rule it produced had to be written in prose in
// design-docs/coach-subscription-lapse.md because the code had nowhere to put it.
//
// Pure, so the rules are unit-testable without instantiating Angular — the same
// constraint the sibling decision helpers here work under.
//
// This module decides only VISIBILITY. Each notice's own answer to "do I have
// something to say right now?" is decided elsewhere (cs-day-items-coach-cards.ts,
// cs-day-items-member-billing-card.ts, cs-day-items-update-card.ts,
// cs-day-items-changelog-card.ts, and plain fields on the component for the rest),
// and arrives here as a boolean.

export enum DayItemsNotice {
  // The Terms / Privacy text changed and this account has not answered the new
  // one yet (#5587). First in the order: it is the only thing on this list that
  // turns into a screen blocking the whole app if it is left unanswered.
  PrivacyUpdate = 'PrivacyUpdate',
  // The coach's own coaching subscription has a money problem (#4393 Surface B,
  // merged with #4455).
  CoachBilling = 'CoachBilling',
  // The member's own membership payment is failing and is being retried
  // (df-6f0a2f8274a3).
  MemberBilling = 'MemberBilling',
  // It has been a while since the last weigh-in.
  WeightWarning = 'WeightWarning',
  // The training plan needs regenerating.
  PlanUpdate = 'PlanUpdate',
  // A workout from an earlier day was started and never finished.
  PendingWorkout = 'PendingWorkout',
  // A newer build of the app is available in the store.
  UpdateAvailable = 'UpdateAvailable',
  // The changelog for the build the user is already on has not been read yet.
  WhatsNew = 'WhatsNew',
  // A weekly checkup was started and never finished.
  UnfinishedWeeklyCheckup = 'UnfinishedWeeklyCheckup',
  // A new user has not done their first workout yet.
  FirstWorkoutReminder = 'FirstWorkoutReminder',
}

/**
 * The order the notices are drawn in, top to bottom.
 *
 * `cs-day-items.html` writes its blocks out by hand, so this array does not by
 * itself decide the order on screen — but `cs-day-items-notice-stack.test.ts`
 * reads the template and fails if the two disagree, which is what keeps this list
 * true rather than decorative.
 */
export const DAY_ITEMS_NOTICE_ORDER: readonly DayItemsNotice[] = [
  DayItemsNotice.PrivacyUpdate,
  DayItemsNotice.CoachBilling,
  DayItemsNotice.MemberBilling,
  DayItemsNotice.WeightWarning,
  DayItemsNotice.PlanUpdate,
  DayItemsNotice.PendingWorkout,
  // UpdateAvailable and WhatsNew share one slot — see the exclusion rule below.
  DayItemsNotice.UpdateAvailable,
  DayItemsNotice.WhatsNew,
  DayItemsNotice.UnfinishedWeeklyCheckup,
  DayItemsNotice.FirstWorkoutReminder,
];

export interface DayItemsNoticeInput {
  /**
   * True when the day on screen is today. No notice card is drawn on any other
   * day: they all talk about what to do now, which is meaningless while the user
   * is looking back at last Tuesday or ahead at next week.
   */
  atToday: boolean;
  /**
   * Each notice's own answer to "do I have something to say right now?", decided
   * by that notice's own rule. Every notice must be present, so adding a member to
   * `DayItemsNotice` is a compile error until the component supplies its answer.
   */
  wantsToShow: Readonly<Record<DayItemsNotice, boolean>>;
}

/**
 * Which notices are actually drawn, in draw order.
 *
 * The coexistence rules, all of them:
 *
 * - Nothing at all is drawn unless the day on screen is today.
 * - The store "Update available" prompt supersedes the "What's new" changelog
 *   nudge. One is forward-looking ("there is a newer version in the store") and
 *   the other backward-looking ("here is what changed in the version you are on"),
 *   so showing both is confusing, and the changelog being looked at is stale
 *   anyway. See design-docs/in-app-update-prompt.md, "Mutual Exclusion with What's
 *   new".
 * - Everything else is independent, and that includes the two billing cards, which
 *   is the pair most likely to be "tidied" into an exclusion by mistake. The coach
 *   card and the member card can never describe the SAME failed payment, because
 *   the member rule requires a `Paddle` licence type and so excludes anybody whose
 *   own membership is paid for by a coaching subscription. But somebody carrying a
 *   residual coach block while paying for their own Paddle membership can see
 *   both, and those are two different subscriptions rather than two copies of one
 *   message. See cs-day-items-member-billing-card.ts, "the coach trap".
 */
export function decideVisibleNotices(input: DayItemsNoticeInput): readonly DayItemsNotice[] {
  // if the day on screen is not today, draw no notice at all
  if (!input.atToday) {
    return [];
  }
  const visible: DayItemsNotice[] = [];
  for (const notice of DAY_ITEMS_NOTICE_ORDER) {
    // if this notice has nothing to say, skip it
    if (!input.wantsToShow[notice]) {
      continue;
    }
    // if the store update prompt is being drawn, the what's-new nudge yields its slot
    if (notice === DayItemsNotice.WhatsNew && input.wantsToShow[DayItemsNotice.UpdateAvailable]) {
      continue;
    }
    visible.push(notice);
  }
  return visible;
}
